import {
  type InternalAttributeName,
  ROUTING_DOMAIN_ATTRIBUTE_NAME,
  SITE_NAME_ATTRIBUTE_NAME,
  STP_DOMAIN_ATTRIBUTE_NAME,
  isInternalAttributeName,
} from './attributes';
import type { DeviceGroupAttribute } from './graphSettings/attributes';

// Reserved labels live under a dedicated name prefix so they never accidentally
// collide with user-created labels. Topology jobs assign them as ipf; users
// may create/assign only the known reserved set below.
export const RESERVED_LABEL_PREFIX = 'ipf_';

// Reserved label produced by site separation. Its value is the device's site name.
export const SITE_LABEL_NAME = 'ipf_site';
export const ROUTING_DOMAIN_LABEL_NAME = 'ipf_routingDomain';
export const STP_DOMAIN_LABEL_NAME = 'ipf_stpDomain';
// Reserved label set by users (manually or via rules) to group devices by
// region. Unlike the labels above, IP Fabric never computes it.
export const REGION_LABEL_NAME = 'ipf_region';

// Reserved labels whose value IP Fabric computes during topology calculation.
export const COMPUTED_RESERVED_LABEL_NAMES = [
  SITE_LABEL_NAME,
  ROUTING_DOMAIN_LABEL_NAME,
  STP_DOMAIN_LABEL_NAME,
] as const;

export type ComputedReservedLabelName =
  (typeof COMPUTED_RESERVED_LABEL_NAMES)[number];

export const ASSIGNABLE_RESERVED_LABEL_NAMES = [
  ...COMPUTED_RESERVED_LABEL_NAMES,
  REGION_LABEL_NAME,
] as const;

export type AssignableReservedLabelName =
  (typeof ASSIGNABLE_RESERVED_LABEL_NAMES)[number];

export const internalAttributeLabelName: Record<
  InternalAttributeName,
  ComputedReservedLabelName
> = {
  [SITE_NAME_ATTRIBUTE_NAME]: SITE_LABEL_NAME,
  [ROUTING_DOMAIN_ATTRIBUTE_NAME]: ROUTING_DOMAIN_LABEL_NAME,
  [STP_DOMAIN_ATTRIBUTE_NAME]: STP_DOMAIN_LABEL_NAME,
};

// Group-by options without an internal attribute are backed by a reserved
// label only (no vAttribute fallback).
const labelOnlyGroupLabelName: Record<
  Exclude<DeviceGroupAttribute, InternalAttributeName>,
  AssignableReservedLabelName
> = {
  region: REGION_LABEL_NAME,
};

export type DeviceGroupSource = {
  labelName: AssignableReservedLabelName;
  /** `vAttribute` name used when the device has no reserved label. */
  attributeName: InternalAttributeName | null;
};

export const deviceGroupSource = (
  groupBy: DeviceGroupAttribute,
): DeviceGroupSource =>
  isInternalAttributeName(groupBy)
    ? { labelName: internalAttributeLabelName[groupBy], attributeName: groupBy }
    : { labelName: labelOnlyGroupLabelName[groupBy], attributeName: null };

const assignableReservedLabelNames = new Set<string>(
  ASSIGNABLE_RESERVED_LABEL_NAMES,
);

export const isReservedLabelName = (name: string): boolean =>
  name.startsWith(RESERVED_LABEL_PREFIX);

export const isAssignableReservedLabelName = (
  name: string,
): name is AssignableReservedLabelName =>
  assignableReservedLabelNames.has(name);

const computedReservedLabelNames = new Set<string>(
  COMPUTED_RESERVED_LABEL_NAMES,
);

export const isComputedReservedLabelName = (
  name: string,
): name is ComputedReservedLabelName => computedReservedLabelNames.has(name);

export const RESERVED_LABEL_NAME_ERROR =
  'The ipf_ prefix is only for reserved labels.';

export const RESERVED_LABEL_CATALOG_MUTATION_ERROR =
  'Reserved label names cannot be renamed or deleted.';

// Region values are short identifiers (e.g. "uk", "eu-west-1", "North
// America"); a value is required because a name-only region groups nothing.
export const REGION_LABEL_VALUE_PATTERN = /^[A-Za-z0-9 _.-]{1,64}$/;

export const REGION_LABEL_VALUE_ERROR =
  'Region value must be 1–64 characters: letters, digits, spaces, "_", "." or "-".';

/**
 * Returns the validation error for a reserved label value, or null when the
 * value is valid. Only reserved labels with a value contract are checked.
 */
export const getReservedLabelValueError = (
  name: string,
  value: string,
): string | null =>
  name === REGION_LABEL_NAME && !REGION_LABEL_VALUE_PATTERN.test(value.trim())
    ? REGION_LABEL_VALUE_ERROR
    : null;

const reservedLabelComputedNoun: Record<ComputedReservedLabelName, string> = {
  [SITE_LABEL_NAME]: 'site name',
  [ROUTING_DOMAIN_LABEL_NAME]: 'routing domain',
  [STP_DOMAIN_LABEL_NAME]: 'STP domain',
};

export const reservedLabelOverrideWarning = (name: string): string => {
  const noun = isComputedReservedLabelName(name)
    ? reservedLabelComputedNoun[name]
    : 'value';
  return `You are overriding the ${noun} computed by IP Fabric.`;
};

// Conflict-resolution weight by assignment source for vLabelAssignment.
// ipf is stronger than inherited and weaker than auto/manual so user
// overrides win. Kept here so backend-shared and topology data providers
// (which cannot import backend-shared) agree.
export const LABEL_SOURCE_PRIORITY = {
  manual: 300,
  auto: 200,
  ipf: 150,
  inherited: 100,
} as const;
